import React, { useMemo } from 'react';
import './BubblesBg.css';

// FNV-style hash — same algorithm as AnimalAvatar so seeded inputs produce
// stable layouts across renders.
const hash = (s) => {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

// Mulberry32 PRNG, seeded from FNV(seed). Deterministic float in [0, 1).
const mulberry32 = (seed) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const lerp = (rand, min, max) => min + rand() * (max - min);

const DENSITIES = {
  mobile: 14,
  desktop: 22,
};

/**
 * Ambient floating squares background. Position: fixed, pointer-events: none,
 * z-index: 0. Honors prefers-reduced-motion by hiding the layer entirely
 * (rule lives in BubblesBg.css).
 */
const BubblesBg = ({ density = 'mobile', seed = 'glacier' }) => {
  const count = DENSITIES[density] ?? DENSITIES.mobile;

  const bubbles = useMemo(() => {
    const rand = mulberry32(hash(`${seed}:${density}`));
    return Array.from({ length: count }, (_, i) => {
      const size = Math.round(lerp(rand, 16, 146));
      const left = lerp(rand, 0, 100);
      const duration = lerp(rand, 12, 42);
      const delay = lerp(rand, -duration, 0); // negative delays distribute starts
      const opacity = lerp(rand, 0.35, 0.75);
      return {
        key: i,
        style: {
          width: `${size}px`,
          height: `${size}px`,
          left: `${left.toFixed(2)}%`,
          animationDuration: `${duration.toFixed(2)}s`,
          animationDelay: `${delay.toFixed(2)}s`,
          opacity,
        },
      };
    });
  }, [count, density, seed]);

  return (
    <div className="glacier-bubbles" aria-hidden="true" data-density={density}>
      {bubbles.map((b) => (
        <span key={b.key} style={b.style} />
      ))}
    </div>
  );
};

export default BubblesBg;
